import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, AudioLines, BookOpen } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { blogPosts } from "@/i18n/blogPosts";
import { useMouseParallax, useTilt } from "@/hooks/useMouseParallax";

/**
 * BLOG HERO — "Signal Frequency"
 * Equalizer bars, featured post with 3D tilt, category chips, mouse spotlight.
 */
const BlogHero = () => {
  const { t, lang } = useLang();
  const { ref, pos } = useMouseParallax();
  const tiltRef = useTilt(10);
  const featured = blogPosts[0];

  const categories = Array.from(new Set(blogPosts.map((p) => p.cat[lang])));

  return (
    <section
      ref={ref}
      data-testid="blog-hero"
      className="relative border-b border-brand-border overflow-hidden min-h-[88vh] flex items-center"
    >
      {/* Aurora + grid */}
      <div className="aurora opacity-35 pointer-events-none">
        <div className="aurora-band" />
      </div>
      <div className="absolute inset-0 hex-grid opacity-30 pointer-events-none" />

      {/* Mouse spotlight */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(500px circle at ${(pos.x + 0.5) * 100}% ${(pos.y + 0.5) * 100}%, rgba(236,72,153,0.18), transparent 60%)`,
        }}
      />

      {/* Background equalizer bars */}
      <div className="absolute inset-x-0 bottom-0 flex items-end justify-center gap-2 h-[35%] opacity-25 pointer-events-none">
        {Array.from({ length: 40 }).map((_, i) => (
          <span
            key={i}
            className="eq-bar w-1 md:w-1.5 rounded-t bg-gradient-to-t from-brand-purple via-brand-pink to-brand-tealLight"
            style={{
              height: `${30 + (i % 7) * 10}%`,
              animationDuration: `${1.1 + ((i * 97) % 9) * 0.13}s`,
              animationDelay: `${(i % 11) * 0.07}s`,
            }}
          />
        ))}
      </div>

      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10 py-20 pt-5 md:py-28 md:pt-7 w-full grid lg:grid-cols-12 gap-12 items-center">
        {/* Left column */}
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full border border-brand-border bg-brand-surface/60 backdrop-blur-sm mb-8">
            <AudioLines className="w-3.5 h-3.5 text-brand-pinkLight animate-pulse" />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-300">
              {lang === "bg" ? "// честота · прайм.фм" : "// frequency · prime.fm"}
            </span>
          </div>

          <p className="text-xs font-mono uppercase tracking-[0.35em] text-brand-purpleLight mb-6 ml-1">{t.blog.tag}</p>

          <h1 className="font-heading font-black uppercase leading-[0.86] tracking-tighter">
            <span className="block text-[3rem] md:text-[5.2rem] lg:text-[6.2rem]">
              {lang === "bg" ? "Маркетинг," : "Marketing,"}
            </span>
            <span className="block text-[3rem] md:text-[5.2rem] lg:text-[6.2rem] chrome-text">
              {lang === "bg" ? "без шум." : "without noise."}
            </span>
          </h1>

          <p className="mt-8 max-w-xl text-zinc-400 leading-relaxed text-base md:text-lg">
            {lang === "bg"
              ? "Анализи, тактики и тенденции от хората на бойната линия. Никакви пух-и-прах — само това, което реално работи."
              : "Analysis, tactics and trends from people on the front line. No fluff — just what actually works."}
          </p>

          {/* Category chips */}
          <div className="mt-8 flex flex-wrap gap-2">
            {categories.map((c, i) => (
              <span
                key={c}
                className="px-3 py-1.5 rounded-full text-[10px] font-mono uppercase tracking-[0.25em] border border-brand-border hover:border-brand-purple/60 hover:text-white text-zinc-300 transition-colors cursor-default"
                style={{ animationDelay: `${i * 80}ms` }}
              >
                {c}
              </span>
            ))}
          </div>

          <div className="mt-10 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-400">
            <span className="w-1.5 h-1.5 rounded-full bg-brand-tealLight animate-pulse" />
            {lang === "bg" ? `${blogPosts.length} статии · актуални` : `${blogPosts.length} articles · updated`}
          </div>
        </div>

        {/* Right column — featured post card (3D tilt) */}
        <div className="lg:col-span-5">
          <Link
            to={`/blog/${featured.slug}`}
            data-testid="blog-hero-featured"
            className="block"
          >
            <div
              ref={tiltRef}
              className="group relative rounded-[24px] overflow-hidden holo-card bg-brand-bg/80 backdrop-blur-xl border border-brand-border transition-transform duration-200 will-change-transform"
              style={{ transformStyle: "preserve-3d" }}
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={featured.image}
                  alt={featured.title[lang]}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-bg via-brand-bg/30 to-transparent" />
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1.5 rounded-full text-[9px] font-mono uppercase tracking-[0.3em] text-white bg-brand-bg/80 border border-brand-purple/50 backdrop-blur-md">
                    {lang === "bg" ? "★ Препоръчано" : "★ Featured"}
                  </span>
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500 mb-3">
                  <span className="text-brand-purpleLight">{featured.cat[lang]}</span>
                  <span>•</span>
                  <span>{featured.time[lang]}</span>
                </div>
                <h3 className="font-heading text-xl md:text-2xl font-bold leading-snug mb-3 group-hover:text-white transition-colors">
                  {featured.title[lang]}
                </h3>
                <p className="text-sm text-zinc-400 leading-relaxed line-clamp-2 mb-5">
                  {featured.excerpt[lang]}
                </p>
                <span className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-brand-pinkLight group-hover:text-white transition-colors">
                  <BookOpen className="w-3.5 h-3.5" />
                  {t.blog.readMore}
                  <ArrowUpRight className="w-4 h-4" />
                </span>
              </div>
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default BlogHero;
